import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import logoIcon from "assets/images/logos/logo-icon.png";
import AuthFlow from "components/auth/AuthFlow";

/**
 * JoinPage - Accept organization invitation
 * SIMPLE FLOW: Load invitation → show accept button → signup → accept → done
 */
const JoinPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignup, setShowSignup] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const acceptingRef = useRef(false);
  const inviteAcceptedRef = useRef(false);
  const INVITE_TOKEN_STORAGE_KEY = "pendingOrganizationInviteToken";
  const INVITE_ROLE_STORAGE_KEY = "pendingOrganizationInviteRole";

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      setCurrentUser(user);
    });
    return unsubscribe;
  }, []);

  // Load invitation details
  useEffect(() => {
    // Always clear any stale token from a previous flow before we validate the
    // new one. Only store after a successful getInvitation response so we never
    // persist an expired, revoked, or already-accepted token.
    sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(INVITE_ROLE_STORAGE_KEY);

    const loadInvitation = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setLoading(false);
        return;
      }

      try {
        const getInvitation = firebase
          .functions()
          .httpsCallable("getInvitation");
        const result = await getInvitation({ token });

        if (result.data.success) {
          sessionStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
          if (result.data.invitation?.role) {
            sessionStorage.setItem(
              INVITE_ROLE_STORAGE_KEY,
              result.data.invitation.role,
            );
          }
          setInvitation(result.data.invitation);
        }
      } catch (err) {
        console.error("Error loading invitation:", err);
        setError(err.message || "Failed to load invitation");
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, [token]);

  // Accept invitation
  const handleAcceptInvitation = async () => {
    if (inviteAcceptedRef.current) {
      setSuccess(true);
      return;
    }
    if (acceptingRef.current) return;
    acceptingRef.current = true;

    setAccepting(true);
    setError("");

    // Wait 1 second for Firestore user doc to be saved (for new signups)
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      const acceptInvitation = firebase
        .functions()
        .httpsCallable("acceptInvitation");
      await acceptInvitation({ token });
      sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(INVITE_ROLE_STORAGE_KEY);
      inviteAcceptedRef.current = true;

      setSuccess(true);
    } catch (err) {
      console.error("Error accepting invitation:", err);
      setError(
        err.message ||
          "Failed to accept invitation. Please refresh and try again."
      );
      acceptingRef.current = false;
      setAccepting(false);
    }
  };

  // After user completes signup, hand off to /onboarding. AccountSetupFlow runs
  // there with autoCreateProfile=true (driven by the token in sessionStorage),
  // creates the user doc, then Onboarding.handleComplete calls acceptInvitation.
  // Calling acceptInvitation here would race ahead of the user doc and 404.
  const handleSignupComplete = async () => {
    navigate("/onboarding");
  };

  // Handler for when user clicks "Accept Invitation" button
  const handleAcceptClick = () => {
    if (currentUser) {
      // User is already logged in, accept immediately
      handleAcceptInvitation();
    } else {
      // User needs to sign up first
      setShowSignup(true);
    }
  };

  // Loading invitation
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Invalid invitation
  if (error) {
    // Check if invitation was revoked
    const isRevoked = error.toLowerCase().includes("revoked");
    const isExpired = error.toLowerCase().includes("expired");
    const isAccepted = error.toLowerCase().includes("accepted");

    let title = "Invalid Invitation";
    let message = error;

    if (isRevoked) {
      title = "Invitation Revoked";
      message =
        "This invitation has been cancelled by the organization administrator. Please contact them if you believe this was a mistake.";
    } else if (isExpired) {
      title = "Invitation Expired";
      message =
        "This invitation has expired. Please request a new invitation from the organization administrator.";
    } else if (isAccepted) {
      title = "Invitation Already Used";
      message =
        "This invitation has already been accepted. If you're having trouble accessing your account, please contact support.";
    } else if (currentUser) {
      title = "Could Not Accept Invitation";
      message = `You're signed in as ${currentUser.email}. This invitation may have been sent to a different account. If you'd like to accept it, please sign out of your current account first.`;
    }

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 3,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 400 }}>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ mb: 2, color: "primary.main" }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
          {currentUser && !isRevoked && !isExpired && !isAccepted && (
            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() =>
                firebase.auth().signOut().then(() => navigate(0))
              }
            >
              Sign Out
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Success - accepted invitation
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 3,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 400 }}>
          <CheckCircleRounded
            sx={{ fontSize: 64, color: "primary.main", mb: 2 }}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            You're all set!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Welcome to {invitation.organizationName}. You&apos;re ready to
            {invitation.role === "marketingExecutive"
              ? " manage zip-slot advertising."
              : " start using ChartMind."}
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate("/dashboard")}
            sx={{ py: 1.5 }}
          >
            Go to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 1: Show accept invitation button (or auto-accept if logged in)
  if (!showSignup) {
    // If user is already logged in, show accepting state
    if (currentUser && accepting) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            p: 3,
          }}
        >
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Accepting invitation...
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 3,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400, textAlign: "center", mb: 4 }}>
          <img
            src={logoIcon}
            alt="FusionMSI"
            style={{ height: 60, marginBottom: 24 }}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Accept Invitation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {invitation.invitedByName} invited you to join{" "}
            <strong>{invitation.organizationName}</strong>
            {invitation.role === "marketingExecutive"
              ? " as a Marketing Executive."
              : invitation.role === "patient"
                ? " on FusionMSI."
                : " on ChartMind."}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {currentUser
              ? "Click below to accept and join the organization."
              : "Sign in or create an account to get started."}
          </Typography>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleAcceptClick}
            sx={{ py: 1.5 }}
          >
            Accept Invitation
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 2: Show signup/login flow
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 400 }}>
        {accepting ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Setting up your account...
            </Typography>
          </Box>
        ) : (
          <AuthFlow
            prefilledEmail={invitation.email}
            skipEmailVerification={true}
            allowSignup={false}
            onSuccess={handleSignupComplete}
          />
        )}
      </Box>
    </Box>
  );
};

export default JoinPage;
